(() => {
  "use strict";

  const tagName = "guru-product-page";
  if (window.customElements.get(tagName)) return;

  const fallbackStrings = Object.freeze({
    addToCart: "Add to cart",
    preOrder: "Pre-order",
    addAndSave: "Add and save __PERCENT__%",
    savePercent: "Save __PERCENT__%",
    inStock: "In stock",
    lowStock: "Low stock · order soon",
    soldOut: "Temporarily sold out",
    unavailable: "Unavailable",
    quantityDecrease: "Decrease by __INCREMENT__",
    quantityIncrease: "Increase by __INCREMENT__",
    variantAnnouncement: "__VARIANT__. __STOCK__. __PRICE__.",
    variantAnnouncementDueNow: "__VARIANT__. __STOCK__. __PRICE__ due at checkout per item.",
  });

  const safeInteger = (value) => {
    if (typeof value === "number") return Number.isSafeInteger(value) ? value : null;
    if (typeof value !== "string" || !/^-?(?:0|[1-9]\d*)$/.test(value)) return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
  };

  const safeNumber = (value) => (
    typeof value === "number" && Number.isFinite(value) ? value : null
  );

  const formatWithDelimiters = (amount, precision, thousands, decimal) => {
    const fixed = (amount / 100).toFixed(precision);
    const [integer, fraction] = fixed.split(".");
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction === undefined ? grouped : `${grouped}${decimal}${fraction}`;
  };

  const themeMoneyFormat = (withCurrency) => {
    const themeSettings = window.themeVariables?.settings || {};
    const format = withCurrency
      ? themeSettings.moneyWithCurrencyFormat
      : themeSettings.moneyFormat;
    return typeof format === "string" && /\{\{\s*amount/.test(format) ? format : null;
  };

  const formatMoney = (amount, withCurrency) => {
    const format = themeMoneyFormat(withCurrency);
    const placeholder = format?.match(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/)?.[1];
    const formats = {
      amount: [2, ",", "."],
      amount_no_decimals: [0, ",", "."],
      amount_with_comma_separator: [2, ".", ","],
      amount_no_decimals_with_comma_separator: [0, ".", ","],
      amount_with_apostrophe_separator: [2, "'", "."],
      amount_with_space_separator: [2, " ", ","],
      amount_no_decimals_with_space_separator: [0, " ", ","],
      amount_with_period_and_space_separator: [2, " ", "."],
      amount_no_decimals_with_period_and_space_separator: [0, " ", "."],
    };

    if (format && placeholder && formats[placeholder]) {
      const [precision, thousands, decimal] = formats[placeholder];
      const result = format.replace(
        /\{\{\s*amount(?:_[a-z_]+)?\s*\}\}/,
        formatWithDelimiters(amount, precision, thousands, decimal),
      );
      return new DOMParser().parseFromString(result, "text/html").body.textContent || "";
    }

    const currency = window.Shopify?.currency?.active || "DKK";
    return new Intl.NumberFormat(document.documentElement.lang || "da-DK", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  const normalizePlan = (raw) => {
    const id = safeInteger(raw?.id);
    const price = safeInteger(raw?.price);
    const compareAtPrice = raw?.compareAtPrice === null ? null : safeInteger(raw?.compareAtPrice);
    const name = typeof raw?.name === "string" ? raw.name.trim() : "";
    const description = typeof raw?.description === "string" ? raw.description.trim() : null;
    const groupId = typeof raw?.groupId === "string"
      ? raw.groupId.trim()
      : Number.isSafeInteger(raw?.groupId) && raw.groupId > 0
        ? String(raw.groupId)
        : "";
    const groupName = typeof raw?.groupName === "string" ? raw.groupName.trim() : "";
    const rawGroupOptions = Array.isArray(raw?.groupOptions) ? raw.groupOptions : null;
    const rawOptions = Array.isArray(raw?.options) ? raw.options : null;
    const rawPriceAdjustments = Array.isArray(raw?.priceAdjustments) ? raw.priceAdjustments : null;
    const perDeliveryPrice = safeInteger(raw?.perDeliveryPrice);
    const unitPrice = raw?.unitPrice === null ? null : safeInteger(raw?.unitPrice);
    const checkoutChargeAmount = safeInteger(raw?.checkoutChargeAmount);
    const remainingBalanceChargeAmount = safeInteger(raw?.remainingBalanceChargeAmount);
    const checkoutChargeValueType = typeof raw?.checkoutCharge?.valueType === "string"
      ? raw.checkoutCharge.valueType.trim()
      : "";
    const checkoutChargeValue = checkoutChargeValueType === "percentage"
      ? safeNumber(raw?.checkoutCharge?.value)
      : safeInteger(raw?.checkoutCharge?.value);
    if (
      id === null
      || id <= 0
      || price === null
      || price < 0
      || (raw?.compareAtPrice !== null && compareAtPrice === null)
      || (compareAtPrice !== null && compareAtPrice < 0)
      || !name
      || description === null
      || !groupId
      || !groupName
      || !rawGroupOptions
      || !rawOptions
      || !rawPriceAdjustments
      || typeof raw?.recurringDeliveries !== "boolean"
      || perDeliveryPrice === null
      || perDeliveryPrice < 0
      || (raw?.unitPrice !== null && unitPrice === null)
      || (unitPrice !== null && unitPrice < 0)
      || checkoutChargeAmount === null
      || checkoutChargeAmount < 0
      || remainingBalanceChargeAmount === null
      || remainingBalanceChargeAmount < 0
      || !["percentage", "price"].includes(checkoutChargeValueType)
      || checkoutChargeValue === null
      || checkoutChargeValue < 0
      || (checkoutChargeValueType === "percentage" && checkoutChargeValue > 100)
      || (checkoutChargeValueType === "price" && checkoutChargeValue !== checkoutChargeAmount)
      || !Number.isSafeInteger(checkoutChargeAmount + remainingBalanceChargeAmount)
      || checkoutChargeAmount + remainingBalanceChargeAmount !== price
      || rawPriceAdjustments.length < 1
      || rawPriceAdjustments.length > 2
    ) return null;
    const groupOptions = rawGroupOptions.map((option, index) => {
      const optionName = typeof option?.name === "string" ? option.name.trim() : "";
      const position = safeInteger(option?.position);
      const values = Array.isArray(option?.values)
        ? option.values.map((value) => typeof value === "string" ? value.trim() : "")
        : null;
      return optionName
        && position === index + 1
        && values
        && values.length > 0
        && values.every(Boolean)
        && new Set(values).size === values.length
        ? { name: optionName, position, values }
        : null;
    });
    const options = rawOptions.map((option, index) => {
      const optionName = typeof option?.name === "string" ? option.name.trim() : "";
      const optionValue = typeof option?.value === "string" ? option.value.trim() : "";
      const position = safeInteger(option?.position);
      return optionName && optionValue && position === index + 1
        ? { name: optionName, position, value: optionValue }
        : null;
    });
    const priceAdjustments = rawPriceAdjustments.map((adjustment, index) => {
      const position = safeInteger(adjustment?.position);
      const orderCount = adjustment?.orderCount === null
        ? null
        : safeInteger(adjustment?.orderCount);
      const valueType = typeof adjustment?.valueType === "string"
        ? adjustment.valueType.trim()
        : "";
      const value = valueType === "percentage"
        ? safeNumber(adjustment?.value)
        : safeInteger(adjustment?.value);
      const adjustmentPrice = safeInteger(adjustment?.price);
      if (
        position !== index + 1
        || (adjustment?.orderCount !== null && (orderCount === null || orderCount < 1))
        || !["percentage", "fixed_amount", "price"].includes(valueType)
        || value === null
        || value < 0
        || (valueType === "percentage" && value > 100)
        || adjustmentPrice === null
        || adjustmentPrice < 0
      ) return null;
      return { position, orderCount, valueType, value, price: adjustmentPrice };
    });
    if (
      groupOptions.some((option) => option === null)
      || options.some((option) => option === null)
      || priceAdjustments.some((adjustment) => adjustment === null)
      || groupOptions.length !== options.length
      || options.some((option, index) => (
        option.position !== groupOptions[index].position
        || option.name !== groupOptions[index].name
        || !groupOptions[index].values.includes(option.value)
      ))
      || priceAdjustments[0].price !== price
      || (priceAdjustments.length === 1 && priceAdjustments[0].orderCount !== null)
      || (priceAdjustments.length === 2 && (
        raw.recurringDeliveries !== true
        || priceAdjustments[0].orderCount === null
        || priceAdjustments[1].orderCount !== null
      ))
    ) return null;
    return {
      id,
      name,
      description,
      groupId,
      groupName,
      groupOptions,
      options,
      recurringDeliveries: raw.recurringDeliveries,
      checkoutCharge: {
        valueType: checkoutChargeValueType,
        value: checkoutChargeValue,
      },
      checkoutChargeAmount,
      remainingBalanceChargeAmount,
      priceAdjustments,
      price,
      compareAtPrice,
      perDeliveryPrice,
      unitPrice,
    };
  };

  const normalizeQuantityPriceBreak = (raw) => {
    const minimumQuantity = safeInteger(raw?.minimumQuantity);
    const price = safeInteger(raw?.price);
    if (!minimumQuantity || minimumQuantity < 1 || price === null || price < 0) return null;
    return { minimumQuantity, price };
  };

  const normalizeVariant = (raw) => {
    const id = safeInteger(raw?.id);
    const price = safeInteger(raw?.price);
    const compareAtPrice = raw?.compareAtPrice === null ? null : safeInteger(raw?.compareAtPrice);
    const quantityMin = safeInteger(raw?.quantityMin);
    const quantityIncrement = safeInteger(raw?.quantityIncrement);
    const quantityMax = raw?.quantityMax === null ? null : safeInteger(raw?.quantityMax);
    const featuredMediaId = raw?.featuredMediaId === null ? null : safeInteger(raw?.featuredMediaId);
    const inventoryQuantity = raw?.inventoryQuantity === null ? null : safeInteger(raw?.inventoryQuantity);
    const rawPriceBreaks = Array.isArray(raw?.quantityPriceBreaks) ? raw.quantityPriceBreaks : null;
    const rawSellingPlans = Array.isArray(raw?.sellingPlans) ? raw.sellingPlans : null;
    if (!rawPriceBreaks || !rawSellingPlans) return null;
    const quantityPriceBreaks = rawPriceBreaks.map(normalizeQuantityPriceBreak);
    const sellingPlans = rawSellingPlans.map(normalizePlan);
    const sellingPlanGroups = new Map();
    for (const plan of sellingPlans) {
      if (!plan) continue;
      const signature = JSON.stringify([plan.groupName, plan.groupOptions]);
      if (sellingPlanGroups.has(plan.groupId) && sellingPlanGroups.get(plan.groupId) !== signature) {
        return null;
      }
      sellingPlanGroups.set(plan.groupId, signature);
    }
    if (
      id === null
      || id <= 0
      || price === null
      || price < 0
      || (raw?.compareAtPrice !== null && compareAtPrice === null)
      || (compareAtPrice !== null && compareAtPrice < 0)
      || !Array.isArray(raw?.options)
      || raw.options.some((option) => typeof option !== "string" || !option)
      || typeof raw?.available !== "boolean"
      || typeof raw?.requiresSellingPlan !== "boolean"
      || !["in", "low", "out", "backorder"].includes(raw?.stockState)
      || typeof raw?.stockText !== "string"
      || !raw.stockText.trim()
      || !quantityMin
      || quantityMin < 1
      || !quantityIncrement
      || quantityIncrement < 1
      || (raw?.quantityMax !== null && quantityMax === null)
      || (quantityMax !== null && quantityMax < quantityMin)
      || (raw?.featuredMediaId !== null && (featuredMediaId === null || featuredMediaId <= 0))
      || (raw?.inventoryQuantity !== null && inventoryQuantity === null)
      || quantityPriceBreaks.some((priceBreak) => priceBreak === null)
      || quantityPriceBreaks.some((priceBreak) => (
        priceBreak.minimumQuantity < quantityMin
        || (quantityMax !== null && priceBreak.minimumQuantity > quantityMax)
        || (priceBreak.minimumQuantity - quantityMin) % quantityIncrement !== 0
      ))
      || new Set(quantityPriceBreaks.map(({ minimumQuantity }) => minimumQuantity)).size !== quantityPriceBreaks.length
      || sellingPlans.some((plan) => plan === null)
      || new Set(sellingPlans.map(({ id: planId }) => planId)).size !== sellingPlans.length
    ) return null;
    return {
      id,
      title: typeof raw.title === "string" ? raw.title : "",
      options: raw.options.map(String),
      available: raw.available === true,
      requiresSellingPlan: raw.requiresSellingPlan === true,
      price,
      compareAtPrice,
      featuredMediaId,
      inventoryQuantity,
      inventoryManagement: typeof raw.inventoryManagement === "string" ? raw.inventoryManagement : "",
      inventoryPolicy: typeof raw.inventoryPolicy === "string" ? raw.inventoryPolicy : "deny",
      stockState: raw.stockState,
      stockText: raw.stockText.trim(),
      sku: typeof raw.sku === "string" ? raw.sku : "",
      quantityMin,
      quantityMax,
      quantityIncrement,
      quantityPriceBreaks: quantityPriceBreaks.sort((left, right) => left.minimumQuantity - right.minimumQuantity),
      url: typeof raw.url === "string" ? raw.url : "",
      sellingPlans,
    };
  };

  class GuruProductPage extends HTMLElement {
    #abortController;
    #mediaObserver;
    #stickyObserver;
    #galleryVisible = true;
    #variantCatalog = [];
    #selectedVariant = null;
    #selectedOptions = [];
    #quantity = 1;
    #form;
    #strings = fallbackStrings;
    #mediaOperationTokens = new WeakMap();
    #expectedMediaOperations = new WeakMap();
    #manuallyPausedMedia = new WeakSet();
    #managedMedia = new Set();
    #reducedMotionQuery;
    #selectedPlanId = null;
    #preferSubscription = false;

    connectedCallback() {
      this.#abortController?.abort();
      this.#mediaObserver?.disconnect();
      this.#stickyObserver?.disconnect();
      this.#galleryVisible = true;
      this.#mediaOperationTokens = new WeakMap();
      this.#expectedMediaOperations = new WeakMap();
      this.#manuallyPausedMedia = new WeakSet();
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;
      this.#form = this.querySelector("form[data-guru-pdp-form]");
      this.#readTranslations();

      this.#bindMotionPreference(signal);
      this.#bindGallery(signal);
      this.#bindPointerFocus(signal);
      this.#bindLinkedProducts(signal);

      if (!this.#form || !this.#readVariantCatalog()) {
        this.#failClosed();
        return;
      }

      this.#selectedOptions = this.#readSelectedOptions();
      const serverVariantId = safeInteger(this.dataset.selectedVariantId);
      const requestedVariant = this.#requestedVariant();
      if (requestedVariant === false) {
        this.#failClosed();
        return;
      }
      this.#selectedVariant = requestedVariant
        || this.#variantCatalog.find(({ id }) => id === serverVariantId)
        || this.#findVariant(this.#selectedOptions)
        || null;

      if (!this.#selectedVariant) {
        this.#failClosed();
        return;
      }

      const requestedPlan = this.#requestedSellingPlan(this.#selectedVariant);
      const normalizeRequestedPlan = requestedPlan === false;
      this.#selectedPlanId = (requestedPlan && requestedPlan !== false ? requestedPlan.id : null)
        || (this.#selectedVariant.requiresSellingPlan ? this.#selectedVariant.sellingPlans[0]?.id : null)
        || null;
      this.#preferSubscription = Boolean(requestedPlan && requestedPlan !== false);

      this.#applyVariantOptions(this.#selectedVariant);
      this.#bindVariantControls(signal);
      this.#bindPurchaseControls(signal);
      this.#bindQuantityControls(signal);
      this.#bindStickyBar(signal);
      this.#render({ updateUrl: normalizeRequestedPlan, announce: false });
      this.dataset.guruPdpReady = "true";
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#mediaObserver?.disconnect();
      this.#stickyObserver?.disconnect();
      this.#mediaObserver = null;
      this.#stickyObserver = null;
      this.#pauseAllMedia();
    }

    #readTranslations() {
      const node = this.querySelector("[data-guru-pdp-i18n]");
      try {
        const parsed = JSON.parse(node?.textContent || "{}");
        this.#strings = Object.freeze(Object.fromEntries(Object.entries(fallbackStrings).map(([key, fallback]) => [
          key,
          typeof parsed?.[key] === "string" && parsed[key] ? parsed[key] : fallback,
        ])));
      } catch (_error) {
        this.#strings = fallbackStrings;
      }
    }

    #copy(key, replacements = {}) {
      let value = this.#strings[key] || fallbackStrings[key] || "";
      Object.entries(replacements).forEach(([token, replacement]) => {
        value = value.replaceAll(`__${token}__`, String(replacement));
      });
      return value;
    }

    #readVariantCatalog() {
      const node = this.querySelector("[data-guru-variant-catalog]");
      if (!node || this.dataset.variantCatalogComplete !== "true") return false;
      try {
        const raw = JSON.parse(node.textContent || "[]");
        const expectedCount = safeInteger(this.dataset.variantCount);
        if (
          !Array.isArray(raw)
          || !expectedCount
          || expectedCount > 250
          || raw.length !== expectedCount
        ) return false;
        const normalized = raw.map(normalizeVariant);
        if (normalized.some((variant) => variant === null)) return false;
        this.#variantCatalog = normalized;
        if (this.#variantCatalog.length < 1) return false;
        const optionCount = this.#variantCatalog[0].options.length;
        if (!optionCount || this.#variantCatalog.some(({ options }) => options.length !== optionCount)) return false;
        const ids = new Set(this.#variantCatalog.map(({ id }) => id));
        const combinations = new Set(this.#variantCatalog.map(({ options }) => JSON.stringify(options)));
        return ids.size === this.#variantCatalog.length
          && combinations.size === this.#variantCatalog.length;
      } catch (_error) {
        return false;
      }
    }

    #requestedVariant() {
      try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has("variant")) return null;
        const requestedId = safeInteger(url.searchParams.get("variant"));
        if (!requestedId) return false;
        return this.#variantCatalog.find(({ id }) => id === requestedId) || false;
      } catch (_error) {
        return false;
      }
    }

    #requestedSellingPlan(variant) {
      try {
        const url = new URL(window.location.href);
        const hasUrlPlan = url.searchParams.has("selling_plan");
        if (hasUrlPlan) {
          const requestedId = safeInteger(url.searchParams.get("selling_plan"));
          if (!requestedId) return false;
          return variant.sellingPlans.find(({ id }) => id === requestedId) || false;
        }
        const serverSelectedId = safeInteger(this.dataset.selectedSellingPlanId);
        if (!serverSelectedId) return null;
        return variant.sellingPlans.find(({ id }) => id === serverSelectedId) || false;
      } catch (_error) {
        return false;
      }
    }

    #failClosed() {
      this.dataset.guruPdpState = "error";
      const button = this.querySelector("[data-main-add]");
      const stickyButton = this.querySelector("[data-sticky-add]");
      [button, stickyButton].forEach((element) => {
        if (!(element instanceof HTMLButtonElement)) return;
        element.disabled = true;
        element.removeAttribute("aria-busy");
      });
      const action = this.querySelector("[data-button-copy]");
      const stickyAction = this.querySelector("[data-sticky-copy]");
      if (action) action.textContent = this.#copy("unavailable");
      if (stickyAction) stickyAction.textContent = this.#copy("unavailable");
      const idInput = this.querySelector("[data-selected-variant-id]");
      const quantityInput = this.querySelector("[data-guru-quantity-input]");
      if (idInput instanceof HTMLInputElement) idInput.disabled = true;
      if (quantityInput instanceof HTMLInputElement) quantityInput.disabled = true;
      this.querySelectorAll("[data-guru-selling-plan-select], [data-guru-purchase-type]").forEach((control) => {
        if (control instanceof HTMLInputElement || control instanceof HTMLSelectElement) control.disabled = true;
      });
    }

    #bindMotionPreference(signal) {
      this.#reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)") || null;
      const applyPreference = () => {
        if (this.#reducedMotionQuery?.matches) this.#pauseAllMedia();
        else this.#autoplayActiveSlide();
      };
      if (this.#reducedMotionQuery?.matches) this.#pauseAllMedia();
      this.#reducedMotionQuery?.addEventListener?.("change", applyPreference, { signal });
    }

    #nextMediaOperation(media, operation) {
      const token = (this.#mediaOperationTokens.get(media) || 0) + 1;
      this.#mediaOperationTokens.set(media, token);
      this.#expectedMediaOperations.set(media, { operation, token });
      return token;
    }

    #pauseMedia(media) {
      this.#nextMediaOperation(media, "pause");
      try {
        media.pause?.();
      } catch (_error) {
        // A provider failure must not break the gallery.
      }

      if (this.#managedMedia.has(media) && !(media instanceof HTMLVideoElement)) {
        try {
          const pauseResult = media.player?.pause?.();
          pauseResult?.catch?.(() => {});
        } catch (_error) {
          // The player proxy can be unavailable while a provider script is loading.
        }
      }
    }

    #playMedia(media, slide) {
      if (this.#manuallyPausedMedia.has(media)) return;
      const token = this.#nextMediaOperation(media, "play");
      this.#managedMedia.add(media);

      if (media.matches?.("video-media")) media.setAttribute("autoplay", "");
      const videos = media instanceof HTMLVideoElement
        ? [media]
        : [...media.querySelectorAll?.("video") || []];
      videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute("muted", "");
        video.setAttribute("playsinline", "");
      });

      try {
        const playResult = media instanceof HTMLVideoElement
          ? media.play?.()
          : media.player?.play?.() || media.play?.();
        Promise.resolve(playResult).then(() => {
          if (
            this.#mediaOperationTokens.get(media) !== token
            || !this.isConnected
            || slide.hidden
            || !this.#galleryVisible
            || this.#reducedMotionQuery?.matches
            || this.#manuallyPausedMedia.has(media)
          ) this.#pauseMedia(media);
        }).catch(() => {});
      } catch (_error) {
        // A blocked or unavailable third-party player must not break the PDP.
      }
    }

    #pauseSlide(slide) {
      slide.querySelectorAll("video-media, model-media").forEach((media) => this.#pauseMedia(media));
      slide.querySelectorAll("video").forEach((video) => this.#pauseMedia(video));
      slide.querySelectorAll("model-viewer").forEach((model) => this.#pauseMedia(model));
    }

    #pauseAllMedia() {
      this.querySelectorAll("[data-gallery-slide]").forEach((slide) => this.#pauseSlide(slide));
    }

    #autoplayActiveSlide() {
      if (
        this.dataset.mediaAutoplay !== "true"
        || !this.#galleryVisible
        || this.#reducedMotionQuery?.matches
      ) return;
      const slide = this.querySelector("[data-gallery-slide]:not([hidden])");
      if (!(slide instanceof HTMLElement)) return;
      slide.querySelectorAll("video-media, model-media").forEach((media) => this.#playMedia(media, slide));
      slide.querySelectorAll(":scope > video").forEach((video) => this.#playMedia(video, slide));
    }

    #bindGallery(signal) {
      const gallery = this.querySelector("[data-pdp-gallery]");
      if (!gallery) return;
      const viewport = gallery.querySelector("[data-gallery-viewport]");
      if (viewport && "IntersectionObserver" in window) {
        this.#galleryVisible = false;
        this.#mediaObserver = new IntersectionObserver((entries) => {
          const entry = entries.find(({ target }) => target === viewport);
          if (!entry) return;
          const visible = entry.isIntersecting && entry.intersectionRatio > 0;
          if (visible === this.#galleryVisible) return;
          this.#galleryVisible = visible;
          if (visible) this.#autoplayActiveSlide();
          else this.#pauseAllMedia();
        }, { threshold: [0, 0.01] });
        this.#mediaObserver.observe(viewport);
      } else {
        this.#galleryVisible = true;
      }

      const mediaForEvent = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return null;
        return target.matches("video-media, model-media, video, model-viewer")
          ? target
          : target.closest("video-media, model-media");
      };
      gallery.addEventListener("media:pause", (event) => {
        const media = mediaForEvent(event);
        if (!media) return;
        const expected = this.#expectedMediaOperations.get(media);
        if (expected?.operation === "pause") {
          this.#expectedMediaOperations.delete(media);
          return;
        }
        const slide = media.closest("[data-gallery-slide]");
        if (slide && !slide.hidden) this.#manuallyPausedMedia.add(media);
      }, { signal });
      gallery.addEventListener("media:play", (event) => {
        const media = mediaForEvent(event);
        if (!media) return;
        const expected = this.#expectedMediaOperations.get(media);
        if (expected?.operation === "play") this.#expectedMediaOperations.delete(media);
        else this.#manuallyPausedMedia.delete(media);
      }, { signal });

      const thumbs = [...gallery.querySelectorAll("[data-gallery-thumb]")];
      thumbs.forEach((thumb, index) => {
        thumb.addEventListener("click", () => this.#activateMedia(thumb.dataset.galleryMediaId), { signal });
        thumb.addEventListener("keydown", (event) => {
          if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (["ArrowRight", "ArrowDown"].includes(event.key)) nextIndex = (index + 1) % thumbs.length;
          if (["ArrowLeft", "ArrowUp"].includes(event.key)) nextIndex = (index - 1 + thumbs.length) % thumbs.length;
          if (event.key === "Home") nextIndex = 0;
          if (event.key === "End") nextIndex = thumbs.length - 1;
          const target = thumbs[nextIndex];
          this.#activateMedia(target?.dataset.galleryMediaId, true);
        }, { signal });
      });
      const activeThumb = thumbs.find((thumb) => thumb.getAttribute("aria-selected") === "true");
      if (activeThumb?.dataset.galleryMediaId) this.#activateMedia(activeThumb.dataset.galleryMediaId);
    }

    #activateMedia(mediaId, focus = false) {
      if (!mediaId) return;
      const thumbs = [...this.querySelectorAll("[data-gallery-thumb]")];
      const slides = [...this.querySelectorAll("[data-gallery-slide]")];
      let activeThumb = null;

      thumbs.forEach((thumb) => {
        const active = thumb.dataset.galleryMediaId === String(mediaId);
        thumb.setAttribute("aria-selected", String(active));
        thumb.tabIndex = active ? 0 : -1;
        if (active) activeThumb = thumb;
      });

      slides.forEach((slide) => {
        const active = slide.dataset.galleryMediaId === String(mediaId);
        slide.hidden = !active;
        slide.setAttribute("aria-hidden", String(!active));
        if (!active) {
          this.#pauseSlide(slide);
        } else if (
          this.dataset.mediaAutoplay === "true"
          && this.#galleryVisible
          && !this.#reducedMotionQuery?.matches
        ) {
          slide.querySelectorAll("video-media, model-media").forEach((media) => this.#playMedia(media, slide));
          slide.querySelectorAll(":scope > video").forEach((video) => this.#playMedia(video, slide));
        }
      });

      if (focus) activeThumb?.focus();
    }

    #bindPointerFocus(signal) {
      this.querySelectorAll("select").forEach((select) => {
        select.addEventListener("pointerdown", () => { select.dataset.pointerFocus = ""; }, { signal });
        select.addEventListener("keydown", () => { delete select.dataset.pointerFocus; }, { signal });
        select.addEventListener("blur", () => { delete select.dataset.pointerFocus; }, { signal });
      });
    }

    #bindLinkedProducts(signal) {
      this.querySelectorAll("select[data-guru-linked-products]").forEach((select) => {
        select.addEventListener("change", () => {
          if (typeof select.value === "string" && select.value) window.location.assign(select.value);
        }, { signal });
      });
    }

    #readSelectedOptions() {
      const groups = [...this.querySelectorAll("[data-guru-option-group]")]
        .sort((left, right) => Number(left.dataset.optionPosition) - Number(right.dataset.optionPosition));
      return groups.map((group) => {
        const select = group.querySelector("[data-guru-option-select]");
        if (select instanceof HTMLSelectElement) return select.value;
        return group.querySelector("[data-guru-option-button][aria-pressed='true']")?.dataset.optionValue || "";
      });
    }

    #applyVariantOptions(variant) {
      this.#selectedOptions = [...variant.options];
      this.querySelectorAll("[data-guru-option-group]").forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        const value = this.#selectedOptions[position];
        const select = group.querySelector("[data-guru-option-select]");
        if (select instanceof HTMLSelectElement) select.value = value;
        group.querySelectorAll("[data-guru-option-button]").forEach((button) => {
          const selected = button.dataset.optionValue === value;
          button.classList.toggle("is-selected", selected);
          button.setAttribute("aria-pressed", String(selected));
        });
      });
    }

    #findVariant(options) {
      return this.#variantCatalog.find((variant) => (
        variant.options.length === options.length
        && variant.options.every((value, index) => value === options[index])
      )) || null;
    }

    #resolveCombination(position, value) {
      const next = [...this.#selectedOptions];
      next[position] = value;
      const exact = this.#findVariant(next);
      if (exact?.available) return exact;

      const reachable = this.#variantCatalog.filter((variant) => (
        variant.options[position] === value
        && variant.options.every((optionValue, index) => (
          index > position || optionValue === next[index]
        ))
      ));
      return reachable.find(({ available }) => available) || exact || reachable[0] || null;
    }

    #bindVariantControls(signal) {
      this.querySelectorAll("[data-guru-option-select]").forEach((select) => {
        select.addEventListener("change", () => {
          const group = select.closest("[data-guru-option-group]");
          const variant = this.#resolveCombination(Number(group?.dataset.optionPosition), select.value);
          if (!variant) return this.#failClosed();
          this.#selectedVariant = variant;
          this.#applyVariantOptions(variant);
          this.#render({ updateUrl: true, announce: true });
        }, { signal });
      });

      this.querySelectorAll("[data-guru-option-button]").forEach((button) => {
        button.addEventListener("click", () => {
          if (button.disabled) return;
          const group = button.closest("[data-guru-option-group]");
          const variant = this.#resolveCombination(Number(group?.dataset.optionPosition), button.dataset.optionValue || "");
          if (!variant) return this.#failClosed();
          this.#selectedVariant = variant;
          this.#applyVariantOptions(variant);
          this.#render({ updateUrl: true, announce: true });
        }, { signal });
      });
    }

    #updateOptionAvailability() {
      const hideSoldOut = this.querySelector("[data-product-variants]")?.dataset.hideSoldOutVariants === "true";
      this.querySelectorAll("[data-guru-option-group]").forEach((group) => {
        const position = Number(group.dataset.optionPosition);
        const matchingVariants = (value) => this.#variantCatalog.filter((variant) => (
          variant.options[position] === value
          && variant.options.every((optionValue, index) => (
            index >= position || optionValue === this.#selectedOptions[index]
          ))
        ));

        group.querySelectorAll("[data-guru-option-button]").forEach((button) => {
          const selected = button.dataset.optionValue === this.#selectedOptions[position];
          const matches = matchingVariants(button.dataset.optionValue);
          const available = matches.some((variant) => variant.available);
          button.disabled = (!matches.length || !available) && !selected;
          button.hidden = hideSoldOut && !available && !selected;
        });
        group.querySelectorAll("[data-guru-option-select] option").forEach((option) => {
          const selected = option.value === this.#selectedOptions[position];
          const matches = matchingVariants(option.value);
          const available = matches.some((variant) => variant.available);
          option.disabled = (!matches.length || !available) && !selected;
          option.hidden = hideSoldOut && !available && !selected;
        });
      });
    }

    #bindPurchaseControls(signal) {
      this.querySelectorAll("[data-guru-purchase-type]").forEach((input) => {
        input.addEventListener("change", () => {
          this.#preferSubscription = false;
          this.#renderPurchaseState({ announce: true });
          if (this.#selectedVariant) this.#updateUrl(this.#selectedVariant);
        }, { signal });
      });
      this.querySelector("[data-guru-selling-plan-select]")?.addEventListener("change", () => {
        const select = this.querySelector("[data-guru-selling-plan-select]");
        this.#selectedPlanId = safeInteger(select?.value);
        this.#renderPurchaseState({ announce: true });
        if (this.#selectedVariant) this.#updateUrl(this.#selectedVariant);
      }, { signal });
    }

    #selectedPlan() {
      return this.#selectedVariant?.sellingPlans.find(({ id }) => id === this.#selectedPlanId) || null;
    }

    #isSubscription() {
      const checked = this.querySelector("[data-guru-purchase-type]:checked");
      return checked?.value === "subscription" || this.#selectedVariant?.requiresSellingPlan === true;
    }

    #renderSellingPlans() {
      const select = this.querySelector("[data-guru-selling-plan-select]");
      const subscriptionInput = this.querySelector("[data-guru-purchase-type][value='subscription']");
      const oneTimeInput = this.querySelector("[data-guru-purchase-type][value='one-time']");
      const oneTimeOption = this.querySelector("[data-one-time-option]");
      const subscriptionStack = subscriptionInput?.closest(".purchase-option-stack");
      if (!(select instanceof HTMLSelectElement) || !(subscriptionInput instanceof HTMLInputElement)) return;

      const plans = this.#selectedVariant.sellingPlans;
      const selectedPlan = plans.find(({ id }) => id === this.#selectedPlanId) || plans[0] || null;
      this.#selectedPlanId = selectedPlan?.id || null;
      const groupedPlans = new Map();
      plans.forEach((plan) => {
        if (!groupedPlans.has(plan.groupId)) groupedPlans.set(plan.groupId, []);
        groupedPlans.get(plan.groupId).push(plan);
      });
      select.replaceChildren(...[...groupedPlans.values()].map((groupPlans) => {
        const group = document.createElement("optgroup");
        group.label = groupPlans[0].groupName;
        group.append(...groupPlans.map((plan) => {
          const option = document.createElement("option");
          option.value = String(plan.id);
          option.textContent = [
            plan.name,
            ...plan.options.map(({ name, value }) => `${name}: ${value}`),
          ].join(" · ");
          if (plan.id === this.#selectedPlanId) option.selected = true;
          return option;
        }));
        return group;
      }));

      const available = plans.length > 0;
      subscriptionInput.disabled = !available;
      if (oneTimeInput instanceof HTMLInputElement) {
        oneTimeInput.disabled = this.#selectedVariant.requiresSellingPlan;
        if (!available) oneTimeInput.checked = true;
      }
      if (oneTimeOption instanceof HTMLElement) oneTimeOption.hidden = this.#selectedVariant.requiresSellingPlan;
      if (subscriptionStack instanceof HTMLElement) {
        subscriptionStack.hidden = !available;
        subscriptionStack.classList.toggle("is-unavailable", !available);
      }
      if ((this.#selectedVariant.requiresSellingPlan || this.#preferSubscription) && available) {
        subscriptionInput.checked = true;
      }
      this.#preferSubscription = false;
    }

    #renderPurchaseState({ announce = false } = {}) {
      const subscription = this.#isSubscription();
      const plan = this.#selectedPlan();
      const select = this.querySelector("[data-guru-selling-plan-select]");
      const controls = this.querySelector("[data-subscription-controls]");
      const planName = this.querySelector("[data-subscription-name]");
      const selectedPlanName = this.querySelector("[data-selling-plan-name]");
      const planOptions = this.querySelector("[data-selling-plan-options]");
      const planPrice = this.querySelector("[data-subscription-price]");
      const planPriceContext = this.querySelector("[data-subscription-price-context]");
      const planSaving = this.querySelector("[data-subscription-saving]");

      if (select instanceof HTMLSelectElement) select.disabled = !subscription || !plan;
      if (controls instanceof HTMLElement) controls.hidden = !subscription || !plan;
      if (planName) planName.textContent = plan?.groupName || "";
      if (selectedPlanName) selectedPlanName.textContent = plan?.name || "";
      if (planOptions) {
        planOptions.textContent = plan?.options
          .map(({ name, value }) => `${name}: ${value}`)
          .join(" · ") || "";
      }
      if (planPrice) planPrice.textContent = plan ? this.#money(plan.checkoutChargeAmount) : "";
      if (planPriceContext instanceof HTMLElement) planPriceContext.hidden = !plan;

      const compareAt = plan?.compareAtPrice && plan.compareAtPrice > plan.price
        ? plan.compareAtPrice
        : null;
      const currentSavingIsOngoing = Boolean(
        plan
        && plan.priceAdjustments.length === 1
        && plan.priceAdjustments[0].orderCount === null
        && plan.remainingBalanceChargeAmount === 0,
      );
      const saving = compareAt && plan && currentSavingIsOngoing
        ? Math.round(((compareAt - plan.price) / compareAt) * 100)
        : 0;
      if (planSaving instanceof HTMLElement) {
        planSaving.hidden = saving <= 0;
        planSaving.textContent = saving > 0 ? this.#copy("savePercent", { PERCENT: saving }) : "";
      }

      const effectivePrice = subscription && plan ? plan.price : this.#oneTimeUnitPrice(this.#selectedVariant);
      const effectiveCompareAt = subscription && plan
        ? compareAt
        : this.#selectedVariant.compareAtPrice > effectivePrice
          ? this.#selectedVariant.compareAtPrice
          : this.#selectedVariant.price > effectivePrice
            ? this.#selectedVariant.price
            : null;
      const selectedPrice = this.querySelector("[data-selected-price]");
      const selectedComparePrice = this.querySelector("[data-selected-compare-price]");
      const singlePrice = this.querySelector("[data-single-price]");
      if (selectedPrice) selectedPrice.textContent = this.#money(effectivePrice);
      if (singlePrice) singlePrice.textContent = this.#money(this.#oneTimeUnitPrice(this.#selectedVariant));
      if (selectedComparePrice instanceof HTMLElement) {
        selectedComparePrice.hidden = !effectiveCompareAt;
        selectedComparePrice.textContent = effectiveCompareAt ? this.#money(effectiveCompareAt) : "";
      }
      if (!this.#syncCommercialDetails(subscription ? plan : null)) {
        this.#failClosed();
        return false;
      }

      const action = this.dataset.preorder === "true"
        ? this.#copy("preOrder")
        : this.#copy("addToCart");
      const dueNowPrice = subscription && plan ? plan.checkoutChargeAmount : effectivePrice;
      if (!this.#renderAction(action, dueNowPrice, { dueAtCheckout: subscription && Boolean(plan) })) return false;
      if (announce) this.#announcePurchaseState(dueNowPrice, subscription ? plan : null);
      return true;
    }

    #announcePurchaseState(unitPrice, plan = null) {
      const announcement = this.querySelector("[data-variant-announcement]");
      if (!announcement || !this.#selectedVariant) return;
      announcement.textContent = this.#copy(plan ? "variantAnnouncementDueNow" : "variantAnnouncement", {
        VARIANT: this.#selectedVariant.title,
        STOCK: this.#selectedVariant.stockText,
        PRICE: this.#money(unitPrice),
      });
    }

    #isValidQuantity(variant, value) {
      return Number.isSafeInteger(value)
        && value >= variant.quantityMin
        && (variant.quantityMax === null || value <= variant.quantityMax)
        && (value - variant.quantityMin) % variant.quantityIncrement === 0;
    }

    #boundedQuantityValues(variant) {
      if (variant.quantityMax === null) return null;
      const optionCount = Math.floor((variant.quantityMax - variant.quantityMin) / variant.quantityIncrement) + 1;
      if (optionCount < 1 || optionCount > 100) return null;
      return Array.from(
        { length: optionCount },
        (_entry, index) => variant.quantityMin + (index * variant.quantityIncrement),
      );
    }

    #stepQuantity(direction) {
      const candidate = this.#quantity + (direction * this.#selectedVariant.quantityIncrement);
      if (!this.#isValidQuantity(this.#selectedVariant, candidate)) return;
      this.#quantity = candidate;
      this.#renderQuantity();
      this.#renderPurchaseState({ announce: true });
    }

    #bindQuantityControls(signal) {
      this.querySelector("[data-quantity-decrease]")?.addEventListener("click", () => {
        this.#stepQuantity(-1);
      }, { signal });

      this.querySelector("[data-quantity-increase]")?.addEventListener("click", () => {
        this.#stepQuantity(1);
      }, { signal });

      this.querySelector("[data-quantity-select]")?.addEventListener("change", (event) => {
        const value = safeInteger(event.currentTarget.value);
        if (this.#isValidQuantity(this.#selectedVariant, value)) this.#quantity = value;
        this.#renderQuantity();
        this.#renderPurchaseState({ announce: true });
      }, { signal });

      this.querySelector("[data-quantity-number]")?.addEventListener("change", (event) => {
        const value = safeInteger(event.currentTarget.value);
        if (this.#isValidQuantity(this.#selectedVariant, value)) this.#quantity = value;
        this.#renderQuantity();
        this.#renderPurchaseState({ announce: true });
      }, { signal });
    }

    #renderQuantity() {
      if (!this.#isValidQuantity(this.#selectedVariant, this.#quantity)) {
        this.#quantity = this.#selectedVariant.quantityMin;
      }
      const values = this.#boundedQuantityValues(this.#selectedVariant);

      const input = this.querySelector("[data-guru-quantity-input]");
      const output = this.querySelector("[data-quantity]");
      const decrease = this.querySelector("[data-quantity-decrease]");
      const increase = this.querySelector("[data-quantity-increase]");
      const select = this.querySelector("[data-quantity-select]");
      const number = this.querySelector("[data-quantity-number]");
      const chevron = this.querySelector("[data-quantity-chevron]");

      if (input instanceof HTMLInputElement) {
        input.value = String(this.#quantity);
        input.disabled = false;
      }
      if (output) output.textContent = String(this.#quantity);
      if (decrease instanceof HTMLButtonElement) {
        decrease.disabled = !this.#isValidQuantity(
          this.#selectedVariant,
          this.#quantity - this.#selectedVariant.quantityIncrement,
        );
        decrease.setAttribute("aria-label", this.#copy("quantityDecrease", { INCREMENT: this.#selectedVariant.quantityIncrement }));
      }
      if (increase instanceof HTMLButtonElement) {
        increase.disabled = !this.#isValidQuantity(
          this.#selectedVariant,
          this.#quantity + this.#selectedVariant.quantityIncrement,
        );
        increase.setAttribute("aria-label", this.#copy("quantityIncrease", { INCREMENT: this.#selectedVariant.quantityIncrement }));
      }
      if (select instanceof HTMLSelectElement) {
        select.hidden = values === null;
        select.replaceChildren(...(values || [this.#selectedVariant.quantityMin]).map((value) => {
          const option = document.createElement("option");
          option.value = String(value);
          option.textContent = String(value);
          option.selected = value === this.#quantity;
          return option;
        }));
      }
      if (number instanceof HTMLInputElement) {
        number.hidden = values !== null;
        number.min = String(this.#selectedVariant.quantityMin);
        number.step = String(this.#selectedVariant.quantityIncrement);
        number.value = String(this.#quantity);
        if (this.#selectedVariant.quantityMax === null) number.removeAttribute("max");
        else number.max = String(this.#selectedVariant.quantityMax);
      }
      if (chevron instanceof SVGElement) chevron.hidden = values === null;
    }

    #oneTimeUnitPrice(variant) {
      return variant.quantityPriceBreaks.reduce(
        (unitPrice, priceBreak) => (
          this.#quantity >= priceBreak.minimumQuantity ? priceBreak.price : unitPrice
        ),
        variant.price,
      );
    }

    #money(amount) {
      return formatMoney(amount, this.dataset.moneyWithCurrency === "true");
    }

    #stockState(variant) {
      return [variant.stockState, variant.stockText];
    }

    #renderAction(action, unitPrice, { dueAtCheckout = false } = {}) {
      const totalAmount = unitPrice * this.#quantity;
      if (
        !Number.isSafeInteger(unitPrice)
        || unitPrice < 0
        || !Number.isSafeInteger(this.#quantity)
        || !Number.isSafeInteger(totalAmount)
        || totalAmount < 0
      ) {
        this.#failClosed();
        return false;
      }
      const total = this.#money(totalAmount);
      const mainCopy = this.querySelector("[data-button-copy]");
      const stickyCopy = this.querySelector("[data-sticky-copy]");
      const mainPrice = this.querySelector("[data-button-price]");
      const stickyPrice = this.querySelector("[data-sticky-price]");
      const mainPriceContext = this.querySelector("[data-button-price-context]");
      const stickyPriceContext = this.querySelector("[data-sticky-price-context]");
      if (mainCopy) mainCopy.textContent = action;
      if (stickyCopy) stickyCopy.textContent = action;
      if (mainPrice) mainPrice.textContent = total;
      if (stickyPrice) stickyPrice.textContent = total;
      if (mainPriceContext instanceof HTMLElement) mainPriceContext.hidden = !dueAtCheckout;
      if (stickyPriceContext instanceof HTMLElement) stickyPriceContext.hidden = !dueAtCheckout;
      return true;
    }

    #replaceFromVariantTemplate(targetSelector, templateSelector, variantId) {
      const target = this.querySelector(targetSelector);
      const template = this.querySelector(`${templateSelector}[data-variant-id="${CSS.escape(String(variantId))}"]`);
      if (!(target instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;
      target.replaceChildren(template.content.cloneNode(true));
    }

    #syncCommercialDetails(plan) {
      const target = this.querySelector("[data-guru-commercial-details]");
      if (!(target instanceof HTMLElement)) return true;
      const selector = plan
        ? `[data-guru-plan-commercial-template][data-variant-id="${CSS.escape(String(this.#selectedVariant.id))}"][data-plan-id="${CSS.escape(String(plan.id))}"]`
        : `[data-guru-commercial-template][data-variant-id="${CSS.escape(String(this.#selectedVariant.id))}"]`;
      const template = this.querySelector(selector);
      if (!(template instanceof HTMLTemplateElement)) return false;
      target.replaceChildren(template.content.cloneNode(true));
      return true;
    }

    #syncVariantFragments(variant) {
      this.#replaceFromVariantTemplate(
        "[data-guru-inventory-detail]",
        "[data-guru-inventory-template]",
        variant.id,
      );
      this.#replaceFromVariantTemplate(
        "[data-guru-product-badges]",
        "[data-guru-variant-badge-template]",
        variant.id,
      );
      this.#replaceFromVariantTemplate(
        "[data-guru-pickup-availability]",
        "[data-guru-pickup-template]",
        variant.id,
      );

      const sku = this.querySelector("[data-selected-sku]");
      if (sku instanceof HTMLElement) {
        sku.hidden = !variant.sku;
        sku.textContent = variant.sku ? `${sku.dataset.skuLabel || "SKU:"} ${variant.sku}` : "";
      }

      const paymentVariant = this.querySelector("[data-guru-payment-variant-id]");
      if (paymentVariant instanceof HTMLInputElement) {
        paymentVariant.value = String(variant.id);
        paymentVariant.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    #render({ updateUrl, announce }) {
      const variant = this.#selectedVariant;
      if (!variant) return this.#failClosed();
      this.dataset.guruPdpState = variant.available ? "available" : "sold-out";
      this.dataset.selectedVariantId = String(variant.id);
      this.#updateOptionAvailability();
      this.#renderSellingPlans();
      this.#renderQuantity();

      const idInput = this.querySelector("[data-selected-variant-id]");
      if (idInput instanceof HTMLInputElement) {
        idInput.value = String(variant.id);
        idInput.disabled = false;
        idInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      this.#syncVariantFragments(variant);

      const [stockState, stockCopy] = this.#stockState(variant);
      const stock = this.querySelector("[data-product-stock]");
      if (stock instanceof HTMLElement) {
        stock.dataset.stockState = stockState;
        const copy = stock.querySelector("span");
        if (copy) copy.textContent = stockCopy;
      }

      const mainButton = this.querySelector("[data-main-add]");
      const stickyButton = this.querySelector("[data-sticky-add]");
      const missingRequiredPlan = variant.requiresSellingPlan && !this.#selectedPlan();
      const canPurchase = variant.available && !missingRequiredPlan;
      [mainButton, stickyButton].forEach((button) => {
        if (button instanceof HTMLButtonElement) button.disabled = !canPurchase;
      });

      if (variant.featuredMediaId) {
        this.#activateMedia(variant.featuredMediaId);
        this.#syncStickyImage(variant.featuredMediaId);
      }

      if (!this.#renderPurchaseState({ announce })) return;
      if (!variant.available) {
        if (!this.#renderAction(this.#copy("soldOut"), this.#oneTimeUnitPrice(variant))) return;
      } else if (missingRequiredPlan && !this.#renderAction(this.#copy("unavailable"), this.#oneTimeUnitPrice(variant))) {
        return;
      }

      if (updateUrl) this.#updateUrl(variant);

      this.#form.dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: { formId: this.#form.id, variant },
      }));
    }

    #syncStickyImage(mediaId) {
      const source = this.querySelector(`[data-gallery-slide][data-gallery-media-id="${CSS.escape(String(mediaId))}"] img`);
      const target = this.querySelector("[data-guru-sticky-image]");
      if (!(source instanceof HTMLImageElement) || !(target instanceof HTMLImageElement)) return;
      ["src", "srcset", "sizes", "width", "height"].forEach((attribute) => {
        const value = source.getAttribute(attribute);
        if (value) target.setAttribute(attribute, value);
      });
    }

    #updateUrl(variant) {
      try {
        const url = new URL(variant.url || this.dataset.productUrl || window.location.href, window.location.origin);
        url.searchParams.set("variant", String(variant.id));
        const plan = this.#isSubscription() ? this.#selectedPlan() : null;
        if (plan) url.searchParams.set("selling_plan", String(plan.id));
        else url.searchParams.delete("selling_plan");
        window.history.replaceState({ path: url.toString() }, "", url.toString());
      } catch (_error) {
        // A malformed merchant URL must not affect selection or purchase.
      }
    }

    #bindStickyBar(signal) {
      const sticky = this.querySelector("[data-sticky-buybar]");
      const mainButton = this.querySelector("[data-main-add]");
      if (!(sticky instanceof HTMLElement) || !(mainButton instanceof HTMLElement)) return;

      this.querySelector("[data-sticky-add]")?.addEventListener("click", () => {
        if (mainButton instanceof HTMLButtonElement && !mainButton.disabled) this.#form.requestSubmit(mainButton);
      }, { signal });

      const update = () => {
        const buttonRect = mainButton.getBoundingClientRect();
        const footer = document.querySelector("footer");
        const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        sticky.hidden = buttonRect.bottom >= 0 || footerVisible;
      };

      if ("IntersectionObserver" in window) {
        this.#stickyObserver = new IntersectionObserver(update, { threshold: [0, 1] });
        this.#stickyObserver.observe(mainButton);
        const footer = document.querySelector("footer");
        if (footer) this.#stickyObserver.observe(footer);
      }
      window.addEventListener("scroll", update, { passive: true, signal });
      window.addEventListener("resize", update, { passive: true, signal });
      update();
    }
  }

  window.customElements.define(tagName, GuruProductPage);
})();
